import apiClient from './apiClient';
import type { QuoteWithClient, QuoteDetail, CreateQuoteRequest, QuoteLineItem, CreateQuoteLineItemRequest, QuoteEvent, QuoteStatus } from '../types/quote';
import type { ApprovalTier, QuoteApproval, PendingApproval } from '../types/approval';

// ── Quote CRUD ──

export const getQuotes = async (): Promise<QuoteWithClient[]> => {
  const response = await apiClient.get<QuoteWithClient[]>('/quotes');
  return response.data;
};

export const getQuote = async (quoteKey: string): Promise<QuoteDetail> => {
  const response = await apiClient.get<QuoteDetail>(`/quotes/${quoteKey}`);
  return response.data;
};

export const createQuote = async (data: CreateQuoteRequest): Promise<QuoteDetail> => {
  const response = await apiClient.post<QuoteDetail>('/quotes', data);
  return response.data;
};

export const updateQuote = async (
  quoteKey: string,
  data: Partial<CreateQuoteRequest & Record<string, unknown>>,
  opts?: { releaseSchedule?: boolean },
): Promise<QuoteDetail> => {
  // P2-4: releaseSchedule=true is sent only after the user confirmed releasing
  // committed work on a Declined/Withdrawn transition (409 SOURCE_RELEASES_COMMITTED).
  const url = opts?.releaseSchedule
    ? `/quotes/${quoteKey}?releaseSchedule=true`
    : `/quotes/${quoteKey}`;
  const response = await apiClient.put<QuoteDetail>(url, data);
  return response.data;
};

export const deleteQuote = async (
  quoteKey: string,
  opts?: { releaseSchedule?: boolean },
): Promise<void> => {
  // releaseSchedule=true is sent only when the user has explicitly confirmed
  // releasing a finalized (live/committed) schedule attached to this quote.
  const url = opts?.releaseSchedule
    ? `/quotes/${quoteKey}?releaseSchedule=true`
    : `/quotes/${quoteKey}`;
  await apiClient.delete(url);
};

export const duplicateQuote = async (quoteKey: string): Promise<QuoteDetail> => {
  const response = await apiClient.post<QuoteDetail>(`/quotes/${quoteKey}/duplicate`);
  return response.data;
};

// ── Quote → Work Order conversion (G2-3, audit #2) ──

export interface ConvertQuoteResult {
  workOrderKey: string;
  workOrderId: string;
  workOrderName: string | null;
  projectKey: string;
  projectId: string;
  detailsCreated: number;
  tasksMigrated: number;
  /** Test lines that could NOT be carried onto the WO — reported, not dropped. */
  unmappedLines: string[];
  /** Tests with no digital module yet — created as placeholder lab tests. */
  placeholderTests: string[];
  commit: {
    bookingsCommitted: number;
    assignmentsCommitted: number;
    scenarioSubmitted: { scenarioKey: string; code: string; name: string } | null;
    draftsSkipped: string[];
  };
}

/**
 * One-click "Create Work Order" from an Accepted quote: creates the WO from
 * the quote's test lines, commits the tentative holds, migrates the schedule
 * onto the WO (no double-count) and flips the quote to 'Converted'.
 * 409 QUOTE_ALREADY_CONVERTED (with workOrderKey) when replayed.
 */
export const convertQuoteToWorkOrder = async (quoteKey: string): Promise<ConvertQuoteResult> => {
  const response = await apiClient.post<ConvertQuoteResult>(`/quotes/${quoteKey}/convert`);
  return response.data;
};

/** The WO + project a Converted quote became — the Converted banner links
 *  there (2026-08-06). Null when the quote was never converted (404). */
export interface ConvertedTarget {
  workOrderKey: string;
  workOrderId: string;
  workOrderName: string | null;
  projectKey: string;
  projectId: string;
  projectName: string | null;
  /** True when that work order has since been deleted (the banner must not present it as live). */
  workOrderDeleted: boolean;
}

export const getQuoteConvertedTarget = async (quoteKey: string): Promise<ConvertedTarget | null> => {
  try {
    const response = await apiClient.get<ConvertedTarget>(`/quotes/${quoteKey}/converted-target`);
    return response.data;
  } catch {
    return null;
  }
};

// ── Manual acceptance (quote lifecycle Phase 1) ──

/** How an out-of-band acceptance arrived. Mirrors the backend enum. */
export type MarkAcceptedChannel = 'verbal' | 'email' | 'signed-offline';

export interface MarkAcceptedPayload {
  acceptedByName: string;
  acceptedByEmail?: string;
  channel: MarkAcceptedChannel;
  note?: string;
}

/**
 * Manually mark a quote Accepted (verbal / email / signed-offline acceptance —
 * including Internal quotes that never go through the public accept link).
 * Allowed from Approved Internal / Sent / Viewed; 409 when already
 * Accepted/Converted. Unlocks the "Create Work Order" conversion button.
 */
export const markQuoteAccepted = async (
  quoteKey: string,
  payload: MarkAcceptedPayload,
): Promise<QuoteDetail> => {
  const response = await apiClient.post<QuoteDetail>(`/quotes/${quoteKey}/mark-accepted`, payload);
  return response.data;
};

// ── Manual status overrides (2026-07-24) ──
// The quote was sent / answered outside the system — these record the real
// outcome with an audited QuoteEvent instead of leaving the status stuck.

/** How the quote left the building, out-of-band. Mirrors the backend enum. */
export type MarkSentChannel = 'email' | 'printed' | 'other';

/** How an out-of-band decline arrived. Mirrors the backend enum. */
export type MarkDeclinedChannel = 'verbal' | 'email' | 'other';

export interface MarkSentPayload {
  channel: MarkSentChannel;
  sentTo?: string;
  note?: string;
}

export interface MarkDeclinedPayload {
  channel: MarkDeclinedChannel;
  reason: string;
  declinedByName?: string;
}

/** Shared response for status-changing overrides — release info when a
 *  Declined/Withdrawn/Expired transition freed schedule bookings. */
export interface StatusChangeResponse extends Partial<QuoteDetail> {
  quote?: Partial<QuoteDetail>;
  scheduleRelease?: { tasksReleased: number; bookingsReleased: number };
  scenariosDeleted?: number;
}

/**
 * Manually mark a quote Sent (it was emailed/printed outside the system).
 * Allowed from Approved Internal only; stamps SentAt and locks the FX rate
 * exactly like the real Send. 409 QUOTE_ALREADY_SENT when replayed.
 */
export const markQuoteSent = async (
  quoteKey: string,
  payload: MarkSentPayload,
): Promise<QuoteDetail> => {
  const response = await apiClient.post<QuoteDetail>(`/quotes/${quoteKey}/mark-sent`, payload);
  return response.data;
};

/**
 * Manually mark a quote Declined (verbal/email decline). Allowed from
 * Approved Internal / Sent / Viewed. Releasing committed work requires the
 * releaseSchedule confirm (409 SOURCE_RELEASES_COMMITTED until confirmed).
 */
export const markQuoteDeclined = async (
  quoteKey: string,
  payload: MarkDeclinedPayload,
  opts?: { releaseSchedule?: boolean },
): Promise<StatusChangeResponse> => {
  const url = opts?.releaseSchedule
    ? `/quotes/${quoteKey}/mark-declined?releaseSchedule=true`
    : `/quotes/${quoteKey}/mark-declined`;
  const response = await apiClient.post<StatusChangeResponse>(url, payload);
  return response.data;
};

/**
 * Admin-only status override: force any status except Converted, with a
 * mandatory reason (logged as a StatusOverridden event). Release statuses
 * keep the committed-work 409 + releaseSchedule ceremony.
 */
export const overrideQuoteStatus = async (
  quoteKey: string,
  payload: { status: QuoteStatus; reason: string },
  opts?: { releaseSchedule?: boolean },
): Promise<StatusChangeResponse> => {
  const url = opts?.releaseSchedule
    ? `/quotes/${quoteKey}/override-status?releaseSchedule=true`
    : `/quotes/${quoteKey}/override-status`;
  const response = await apiClient.post<StatusChangeResponse>(url, payload);
  return response.data;
};

// ── Line Items ──

export const getQuoteLineItems = async (quoteKey: string): Promise<QuoteLineItem[]> => {
  const response = await apiClient.get<QuoteLineItem[]>(`/quotes/${quoteKey}/line-items`);
  return response.data;
};

export const createQuoteLineItem = async (quoteKey: string, data: CreateQuoteLineItemRequest): Promise<QuoteLineItem> => {
  const response = await apiClient.post<QuoteLineItem>(`/quotes/${quoteKey}/line-items`, data);
  return response.data;
};

export const updateQuoteLineItem = async (lineItemKey: string, data: Partial<QuoteLineItem>): Promise<QuoteLineItem> => {
  const response = await apiClient.put<QuoteLineItem>(`/quotes/line-items/${lineItemKey}`, data);
  return response.data;
};

export const deleteQuoteLineItem = async (lineItemKey: string): Promise<void> => {
  await apiClient.delete(`/quotes/line-items/${lineItemKey}`);
};

export const addLineItemsFromCatalog = async (quoteKey: string, catalogItemKeys: string[]): Promise<QuoteLineItem[]> => {
  const response = await apiClient.post<QuoteLineItem[]>(`/quotes/${quoteKey}/line-items/from-catalog`, { catalogItemKeys });
  return response.data;
};

// ── PDF ──

export const downloadQuotePdf = async (quoteKey: string, quoteNumber: string): Promise<void> => {
  const response = await apiClient.get(`/quotes/${quoteKey}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${quoteNumber}.pdf`;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const previewQuotePdf = async (quoteKey: string): Promise<string> => {
  const response = await apiClient.get(`/quotes/${quoteKey}/pdf`, { responseType: 'blob' });
  return window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
};

// ── Approvals ──

export const getApprovalTiers = async (): Promise<ApprovalTier[]> => {
  const response = await apiClient.get<ApprovalTier[]>('/quotes/approval-tiers');
  return response.data;
};

export const updateApprovalTier = async (key: string, data: Partial<ApprovalTier>): Promise<ApprovalTier> => {
  const response = await apiClient.put<ApprovalTier>(`/quotes/approval-tiers/${key}`, data);
  return response.data;
};

export const submitForApproval = async (quoteKey: string): Promise<QuoteApproval> => {
  const response = await apiClient.post<QuoteApproval>(`/quotes/${quoteKey}/submit-approval`);
  return response.data;
};

export const approveQuote = async (quoteKey: string, comment?: string): Promise<QuoteApproval> => {
  const response = await apiClient.post<QuoteApproval>(`/quotes/${quoteKey}/approve`, { comment });
  return response.data;
};

export const rejectQuote = async (quoteKey: string, reason: string): Promise<QuoteApproval> => {
  const response = await apiClient.post<QuoteApproval>(`/quotes/${quoteKey}/reject`, { reason });
  return response.data;
};

export const getPendingApprovals = async (): Promise<PendingApproval[]> => {
  const response = await apiClient.get<PendingApproval[]>('/quotes/pending-approvals');
  return response.data;
};

export const getQuoteApproval = async (quoteKey: string): Promise<QuoteApproval | null> => {
  const response = await apiClient.get<QuoteApproval | null>(`/quotes/${quoteKey}/approval`);
  return response.data || null;
};

// ── Quote-create policy (REQ-02) ──

export const getQuoteCreatePolicy = async (): Promise<{ minRole: string }> => {
  const response = await apiClient.get<{ minRole: string }>('/quotes/create-policy');
  return response.data;
};

export const updateQuoteCreatePolicy = async (minRole: string): Promise<{ minRole: string }> => {
  const response = await apiClient.put<{ minRole: string }>('/quotes/create-policy', { minRole });
  return response.data;
};

// ── Sending ──

export const sendQuoteToClient = async (quoteKey: string, data: {
  recipientEmails: string[];
  subject: string;
  body: string;
}): Promise<{ token: string }> => {
  const response = await apiClient.post<{ token: string }>(`/quotes/${quoteKey}/send`, data);
  return response.data;
};

export const getQuoteEvents = async (quoteKey: string): Promise<QuoteEvent[]> => {
  const response = await apiClient.get<QuoteEvent[]>(`/quotes/${quoteKey}/events`);
  return response.data;
};
